
import MarketAnalysisChart from "@/components/marketAnalysis/MarketAnalysisChart";
import MarketAnalysisFAQ from "@/components/marketAnalysis/MarketAnalysisFAQ";
import MarketAnalysisKpis from "@/components/marketAnalysis/MarketAnalysisKpis";
import MarketAnalysisPerformance from "@/components/marketAnalysis/MarketAnalysisPerformance";
import MarketSummaryCard from "@/components/marketAnalysis/MarketSummaryCard";
import PersonalizedMarketReportCard from "@/components/marketAnalysis/PersonalizedMarketReportCard";
import SnippetsLibraryContainer from "@/components/marketAnalysis/SnippetsLibraryContainer";
import { tabConstants } from '@/constants/marketAnalysis';
import subscriptionConstants from '@/constants/subscription.constants';
import { backendExceptionHandler } from "@/mixins";
import MarketAnalysisService from "@/service//MarketAnalysisService";

const { subscribeProductKeys } = subscriptionConstants;
const enabledPersonalizedReportFeature = process.env.ENABLED_PERSONALIZED_REPORT_FEATURE === 'true';

export default {
    name: 'MarketAnalysisPage',

    components: {
        MarketSummaryCard,
        MarketAnalysisKpis,
        MarketAnalysisPerformance,
        MarketAnalysisChart,
        PersonalizedMarketReportCard,
        MarketAnalysisFAQ,
        SnippetsLibraryContainer,
    },

    mixins: [backendExceptionHandler],

    props: {
        pageKey: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            activeTab: null,
            marketSummaryContent: null,
            marketSummaryCreatedAt: '',
            snippetDetailsTitle: false,
        }
    },

    async fetch() {
        let locale = this.$getFallbackLocale()
        const routeLang = this.$route.path.split('/')?.[1];
        if (this.$getAvailableLocaleCodes().includes(routeLang)) {
            locale = routeLang
        }

        this.$setLocale(locale)
        // only general market report tab should be rendered by server side
        if (this.activeTab !== tabConstants.report) {
            return
        }

        try {
            const dateOnUrl = this.$route.params.date
            const res = dateOnUrl ?
                await this.marketAnalysisService.getMarketSummaryByDate(dateOnUrl, locale) :
                await this.marketAnalysisService.getLastMarketSummary(locale)

            if (res.body) {
                this.marketSummaryContent = res.body
                this.marketSummaryCreatedAt = res.created
            } else {
                this.marketSummaryContent = null
                this.marketSummaryCreatedAt = ''
            }
        } catch (error) {            
            this.marketSummaryContent = null
            this.marketSummaryCreatedAt = ''
        }
    },

    head() {
        return {
            titleTemplate: `%s | ${this.$t('pages.marketAnalysis.head.titleTemplate')}`,
            title: this.$t('pages.marketAnalysis.head.title'),
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.$t('pages.marketAnalysis.head.description'),
                },
                {
                    hid: 'og:title',
                    name: 'og:title',
                    content: this.$t('pages.marketAnalysis.head.ogTitle'),
                },
                {
                    hid: 'og:description',
                    name: 'og:description',
                    content: this.$t('pages.marketAnalysis.head.ogDescription'),
                },
                {
                    hid: 'twitter:title',
                    name: 'twitter:title',
                    content: this.$t('pages.marketAnalysis.head.twitterTitle'),
                },
                {
                    hid: 'twitter:description',
                    name: 'twitter:description',
                    content: this.$t('pages.marketAnalysis.head.twitterDescription'),
                }
            ],
        }
    },

    computed: {
        isLoggedIn() {
            return !!this.$store.state.account.user
        },
        isSubscribedFinAgent() {
            return this.isLoggedIn && this.$store.state.account.subscribedProductIds?.includes(subscribeProductKeys.finAgent);
        },
        showPersonalizedTab() {
            return this.enabledPersonalizedReportFeature && this.isSubscribedFinAgent;
        },
        homeIconPath() {
            return this.$vuetify.theme.dark ?
                require('@/assets/icons/home-dark.svg') :
                require('@/assets/icons/home-light.svg');
        },
    },

    watch: {
        pageKey: {
            immediate: true,
            handler(pageKey) {
                this.checkAccessLibraryTab();
                this.activeTab = pageKey;
            }
        },
        isLoggedIn(state) {
            this.checkAccessLibraryTab();
            if (state) {
                this.$nextTick(() => this.activeTab = this.pageKey);
            }
        },
    },

    created() {
        this.tabConstants = tabConstants
        this.marketAnalysisService = new MarketAnalysisService(this.$axios);
        this.enabledPersonalizedReportFeature = enabledPersonalizedReportFeature;
    },

    methods: {
        checkAccessLibraryTab() {
            if (this.isLoggedIn && !this.isSubscribedFinAgent && this.pageKey === tabConstants.library) {
                this.$router.push({ path: this.$getTranslatedRoutePath('/market-analysis/')});
            }
        },
    }
}
